import { UPLOAD_WORKER_URL } from '@/lib/env'

async function workerFetch<T>(
  path: string,
  options: RequestInit & { sessionToken?: string } = {},
): Promise<T> {
  if (!UPLOAD_WORKER_URL) {
    throw new Error('Upload worker URL is not configured. Set VITE_UPLOAD_WORKER_URL.')
  }

  const headers = new Headers(options.headers)
  if (!headers.has('Content-Type') && options.body) headers.set('Content-Type', 'application/json')
  if (options.sessionToken) headers.set('Authorization', `Bearer ${options.sessionToken}`)

  const res = await fetch(`${UPLOAD_WORKER_URL}${path}`, {
    ...options,
    headers,
  })
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText)
    throw new Error(`Contributor worker error: ${text}`)
  }
  return res.json() as Promise<T>
}

export interface ContributorSession {
  sessionToken: string
  contributorId: string
  dropId: string
  expiresAt: number
}

export async function createContributorSession(rawToken: string): Promise<ContributorSession> {
  const data = await workerFetch<{
    sessionToken?: string
    token?: string
    contributorId: string
    dropId: string
    expiresAt: number
  }>('/contributor/session', {
    method: 'POST',
    body: JSON.stringify({ token: rawToken, rawToken }),
  })
  return {
    sessionToken: data.sessionToken ?? data.token ?? '',
    contributorId: data.contributorId,
    dropId: data.dropId,
    expiresAt: data.expiresAt,
  }
}

export async function fetchContributorDrop(
  sessionToken: string,
  options: { includePhotos?: boolean } = {},
) {
  const params = new URLSearchParams()
  if (options.includePhotos) params.set('includePhotos', 'true')
  const suffix = params.toString() ? `?${params.toString()}` : ''
  return workerFetch<ContributorDropResponse>(`/contributor/drop${suffix}`, {
    method: 'GET',
    sessionToken,
  })
}

export interface ContributorGalleryPhoto {
  id: string
  drop_id?: string
  dropId?: string
  original_url?: string
  originalUrl?: string
  thumbnail_url?: string | null
  thumbnailUrl?: string | null
  processed_url?: string | null
  processedUrl?: string | null
  caption?: string | null
  uploaded_at?: string
  uploadedAt?: string
  processing_status?: string | null
  processingStatus?: string | null
  width?: number | null
  height?: number | null
}

export interface ContributorDropResponse {
  contributor?: {
    id: string
    drop_id?: string
    dropId?: string
    revoked_at?: string | null
    revokedAt?: string | null
    drop: ContributorDrop
  }
  contributorId?: string
  dropId?: string
  drop?: ContributorDrop
}

export interface ContributorDrop {
  id: string
  name: string
  description: string | null
  status: string
  opens_at?: string
  opensAt?: string
  closes_at?: string
  closesAt?: string
  time_limit_hours?: number
  timeLimitHours?: number
  cover_photo_url?: string | null
  coverPhotoUrl?: string | null
  is_trial?: boolean | null
  isTrial?: boolean | null
  organization: {
    id: string
    tier: string | null
    branding_config?: Record<string, unknown> | null
    brandingConfig?: Record<string, unknown> | null
  }
  photos?: ContributorGalleryPhoto[]
}

export interface UploadIntentParams {
  sessionToken: string
  filename: string
  contentType: string
  sizeBytes: number
  caption?: string
  fileHash?: string
}

export interface UploadIntent {
  uploadUrl: string
  key: string
  photoId: string
  uploadCompletionToken: string
  uploadCompleteToken?: string
  originalUrl?: string
}

export async function createUploadIntent(params: UploadIntentParams): Promise<UploadIntent> {
  const data = await workerFetch<UploadIntent & { uploadCompleteToken?: string }>('/contributor/upload-intent', {
    method: 'POST',
    sessionToken: params.sessionToken,
    body: JSON.stringify({
      filename: params.filename,
      contentType: params.contentType,
      sizeBytes: params.sizeBytes,
      caption: params.caption,
      fileHash: params.fileHash,
    }),
  })

  const uploadCompletionToken = data.uploadCompletionToken ?? data.uploadCompleteToken
  if (!uploadCompletionToken) {
    throw new Error('Contributor worker response did not include an upload completion token.')
  }

  return {
    ...data,
    uploadCompletionToken,
  }
}

export function completeUpload(params: {
  key: string
  uploadCompletionToken: string
  caption?: string
}): Promise<{ photoId?: string }> {
  return workerFetch<{ photoId?: string }>('/contributor/upload-complete', {
    method: 'POST',
    body: JSON.stringify(params),
  })
}

export function submitContributorReport(params: {
  sessionToken: string
  photoId: string
  reason: string
}) {
  return workerFetch<{ submitted: boolean }>('/contributor/report', {
    method: 'POST',
    sessionToken: params.sessionToken,
    body: JSON.stringify({
      photoId: params.photoId,
      reason: params.reason,
    }),
  })
}
